import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditCustomItemModal from '@modals/CreateOrEditCustomItemModal'
import CreateOrEditCustomItemGroupModal from '@modals/CreateOrEditCustomItemGroupModal'

import type { CustomItemGroupModel, CustomItemModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateOrEditCustomItemGroupModal: () => void,
    showCreateOrEditCustomItemModal: () => void,
  },
}

const defaultState = {
  showCreateOrEditCustomItemGroupModal: false,
  showCreateOrEditCustomItemModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditCustomItemGroupModal: {
      closeModal: () => setState({ showCreateOrEditCustomItemGroupModal: false }),
      createCustomItemGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCustomItemGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteCustomItemGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCustomItemModal: {
      closeModal: () => setState({ showCreateOrEditCustomItemModal: false }),
      createCustomItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCustomItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditCustomItemGroupModal, showCreateOrEditCustomItemModal } = state

  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditCustomItemGroupModal: (payload: { customItemGroup: CustomItemGroupModel }) => {
        setModalData('CreateOrEditCustomItemGroupModal', payload)
        setState({ showCreateOrEditCustomItemGroupModal: true })
      },
      showCreateOrEditCustomItemModal: (payload: { customItem: CustomItemModel }) => {
        setModalData('CreateOrEditCustomItemModal', payload)
        setState({ showCreateOrEditCustomItemModal: true })
      },
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditCustomItemGroupModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCustomItemGroupModal', setState)}
          showModal={showCreateOrEditCustomItemGroupModal}
        />

        <CreateOrEditCustomItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCustomItemModal', setState)}
          showModal={showCreateOrEditCustomItemModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
